import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { HelpCircle, MessageCircle, ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";

export default function SupportButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // list | new | detail
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState({ subject: "", message: "", category: "bug" });
  const [reply, setReply] = useState("");
  const endRef = useRef(null);

  const loadList = async () => {
    try { const { data } = await api.get("/support/tickets/mine"); setTickets(data); }
    catch (e) { console.warn("support tickets load failed", e); }
  };
  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setActive(data.ticket); setMessages(data.messages); setView("detail");
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => { if (open) loadList(); }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => {
    if (!active || view !== "detail") return;
    const t = setInterval(() => loadDetail(active.id), 6000);
    return () => clearInterval(t);
  }, [active?.id, view]);

  const submitNew = async () => {
    if (!form.subject || !form.message) { toast.error("Rellena asunto y mensaje"); return; }
    try {
      const { data } = await api.post("/support/tickets", form);
      toast.success("Enviado. Te contestaremos en breve.");
      setForm({ subject: "", message: "", category: "bug" });
      await loadDetail(data.id);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const submitReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/support/tickets/${active.id}/messages`, { message: reply });
      setReply("");
      loadDetail(active.id);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const cat = { bug: "Fallo", question: "Duda", payment: "Pago", account: "Cuenta", other: "Otro" };
  const statusCls = { open: "bg-amber-100 text-amber-800", closed: "bg-gray-100 text-gray-500" };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="rounded-full font-display gap-2 border border-[#0B1D3A]/15" data-testid="help-btn">
          <HelpCircle size={16}/> Ayuda
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl flex items-center gap-3">
            {view !== "list" && (
              <button onClick={() => { setView("list"); loadList(); }} className="text-[#5C6B7D]" data-testid="help-back-btn"><ArrowLeft size={20}/></button>
            )}
            <MessageCircle className="hub-coral"/> {view === "detail" ? active?.subject : view === "new" ? "Nuevo mensaje" : "Centro de ayuda"}
          </DialogTitle>
        </DialogHeader>

        {view === "list" && (
          <div className="space-y-3 overflow-y-auto flex-1">
            <p className="text-sm text-[#5C6B7D]">¿Algún fallo o duda? Cuéntanoslo. Te contestamos rápido.</p>
            <Button onClick={() => setView("new")} className="hub-btn-primary w-full gap-2" data-testid="help-new-btn">
              <MessageCircle size={14}/> Enviar mensaje al equipo
            </Button>
            {tickets.length > 0 && <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display pt-2">Tus conversaciones</div>}
            {tickets.map(t => (
              <button key={t.id} onClick={() => loadDetail(t.id)} className="hub-card p-3 w-full text-left" data-testid={`help-ticket-${t.id}`}>
                <div className="flex items-center gap-2">
                  <span className={`hub-pill ${statusCls[t.status] || statusCls.open}`}>{t.status === "open" ? "Abierto" : "Cerrado"}</span>
                  <span className="hub-pill hub-sand text-[#0B1D3A]">{cat[t.category] || t.category}</span>
                  {t.unread_user && <span className="hub-pill bg-[#FF6B57] text-white">Nueva respuesta</span>}
                </div>
                <div className="font-display font-semibold text-sm mt-1">{t.subject}</div>
                <div className="text-xs text-[#5C6B7D]">{new Date(t.updated_at).toLocaleString("es-ES")}</div>
              </button>
            ))}
          </div>
        )}

        {view === "new" && (
          <div className="space-y-3 overflow-y-auto flex-1">
            <div>
              <Label>Categoría</Label>
              <select value={form.category} onChange={e=>setForm({...form, category: e.target.value})} className="w-full mt-1 h-10 border border-[#0B1D3A]/15 rounded-md px-3" data-testid="help-category">
                {Object.entries(cat).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label>Asunto</Label>
              <Input value={form.subject} onChange={e=>setForm({...form, subject: e.target.value})} placeholder="Ej: No me sale el botón de pagar" data-testid="help-subject"/>
            </div>
            <div>
              <Label>Mensaje</Label>
              <Textarea value={form.message} onChange={e=>setForm({...form, message: e.target.value})} placeholder="Cuéntanos qué ocurre..." rows={5} data-testid="help-message"/>
            </div>
            <Button onClick={submitNew} className="hub-btn-primary w-full" data-testid="help-submit-btn">Enviar</Button>
          </div>
        )}

        {view === "detail" && active && (
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center gap-2 py-2 border-b border-[#0B1D3A]/10">
              <span className={`hub-pill ${statusCls[active.status] || statusCls.open}`}>{active.status === "open" ? "Abierto" : "Cerrado"}</span>
              <span className="hub-pill hub-sand text-[#0B1D3A]">{cat[active.category] || active.category}</span>
            </div>
            <div className="flex-1 overflow-y-auto py-3 space-y-2">
              {messages.map(m => {
                const mine = m.sender_id === user?.id;
                const admin = m.sender_role === "admin";
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${mine ? "bg-[#0B1D3A] text-white" : admin ? "bg-[#FF6B57]/10 border border-[#FF6B57]/30 text-[#0B1D3A]" : "bg-white border border-[#0B1D3A]/10"}`}>
                      {!mine && <div className="text-[10px] uppercase tracking-widest opacity-70 font-display mb-1">{m.sender_name} {admin ? "· MarinaHub" : ""}</div>}
                      <div className="text-sm whitespace-pre-wrap">{m.message}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef}/>
            </div>
            {active.status === "open" && (
              <div className="flex gap-2 pt-2 border-t border-[#0B1D3A]/10">
                <Input value={reply} onChange={e=>setReply(e.target.value)} onKeyDown={e=>e.key==="Enter" && submitReply()} placeholder="Escribe una respuesta..." className="rounded-full" data-testid="help-reply-input"/>
                <Button onClick={submitReply} className="hub-btn-primary rounded-full gap-2" data-testid="help-reply-btn"><Send size={14}/></Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
